import { Injectable } from '@angular/core';
import { ApiService } from './api';
import { AuthResponse } from '../models/auth-response.model';
import { AccountHelper } from 'src/lib/accountHelper';
import { LocalStorageService } from './local-storage';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { UserManager } from './managers/user.manager';
import { BiometryType, NativeBiometric } from "capacitor-native-biometric";

@Injectable({
  providedIn: 'root'
})
export class AuthenticateService {
  private keychainId = 'com.nextgenrodeo.app';
  constructor(
    private api: ApiService,
    private localStorage: LocalStorageService,
    private modalCtrl: ModalController,
    private userManager: UserManager,
    private router: Router) { }

  async authenticate(emailAddress: string, password: string) {
    return await this.api.postWithoutAuth<AuthResponse>(`${this.api.endpoints.authenticate}/token`, { emailAddress, password });
  }

  async logout() {
    const promise = this.api.postWithAuth(
      this.api.endpoints.authenticate + '/revokeToken'
    ).catch(() => { });

    AccountHelper.instance().clearToken();
    this.localStorage.purge();
    this.userManager.clearUser();
    const openModal = await this.modalCtrl.getTop();
    if (openModal && openModal.canDismiss) {
      openModal.dismiss();
    }
    this.router.navigate(['auth/login'], { replaceUrl: true });

    await promise;
  }

  async refreshUserData() {
    return await this.api.getWithAuth<AuthResponse>(this.api.endpoints.authenticate + '/refreshAuthResponse');
  }

  async performBiometricVerificatin() {
    if (!(await this.isFaceIdAvailable())) return;

    const credentials = await NativeBiometric.getCredentials({
      server: this.keychainId,
    });

    return credentials;
  }

  async saveCredentials(username, password) {
    if (!(await this.isFaceIdAvailable(false))) {
      return;
    }

    // console.log({newCreds: { username, password, server: this.keychainId }});
    // Save user's credentials
    await NativeBiometric.setCredentials({
      username,
      password,
      server: this.keychainId
    });

    await this.localStorage.setItem('FaceIdOptIn', 'true');
    // console.log('saved');
  }

  async disableFaceId() {
    // Delete user's credentials
    await NativeBiometric.deleteCredentials({
      server: this.keychainId
    });
    // console.log('cleared');

    await this.localStorage.removeItem('FaceIdOptIn');
  }

  async isFaceIdAvailable(invokeFaceId = true) {
    const result = await NativeBiometric.isAvailable();
    // console.log({ result });
    if (!result.isAvailable) {
      throw new Error('Native Biometric Not Available');
    }

    const isFaceID = result.biometryType == BiometryType.FACE_ID;

    if (invokeFaceId) {
      const verified = await NativeBiometric.verifyIdentity({
        reason: "For easy log in",
        title: "Log in",
        subtitle: "Your credentials will be stored on your device.",
        description: "These can be viewed and managed from your device settings.",
      })
      .then(() => true)
      .catch(() => false);
  
      // console.log({ verified });
      return verified;
    } else {
      return true;
    }
  }
}
